import { useState, useEffect, useCallback, useRef } from "react";
import { API } from "@/App";

const STATUS_CONFIG = {
  online: { color: "bg-emerald-500", glow: "shadow-[0_0_6px_rgba(16,185,129,0.8)]", label: "ONLINE" },
  degraded: { color: "bg-amber-500", glow: "shadow-[0_0_6px_rgba(245,158,11,0.8)]", label: "SLOW / BUSY" },
  offline: { color: "bg-red-500", glow: "shadow-[0_0_6px_rgba(239,68,68,0.8)]", label: "OFFLINE" },
  checking: { color: "bg-muted-foreground", glow: "", label: "CHECKING..." },
};

export const ServerStatus = () => {
  const [status, setStatus] = useState("checking");
  const [latency, setLatency] = useState(null);
  const timerRef = useRef(null);

  const checkStatus = useCallback(async () => {
    const start = Date.now();
    try {
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 8000);
      const res = await fetch(`${API}/`, { signal: controller.signal });
      clearTimeout(timeout);
      const ms = Date.now() - start;
      setLatency(ms);
      if (res.status === 429 || ms > 3000) {
        setStatus("degraded");
      } else if (res.ok) {
        setStatus("online");
      } else {
        setStatus("offline");
      }
    } catch {
      setLatency(null);
      setStatus("offline");
    }
  }, []);

  useEffect(() => {
    checkStatus();
    timerRef.current = setInterval(checkStatus, 30000);
    const onVisible = () => {
      if (document.visibilityState === "visible") checkStatus();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      clearInterval(timerRef.current);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [checkStatus]);

  const cfg = STATUS_CONFIG[status];

  return (
    <div
      className="flex items-center gap-2 px-4 py-2 border-t border-border/40"
      data-testid="server-status-indicator"
      title={latency !== null ? `Server response: ${latency}ms` : "Server unreachable"}
    >
      <span className="relative flex h-2.5 w-2.5">
        {status === "online" && (
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-40" />
        )}
        <span className={`relative inline-flex rounded-full h-2.5 w-2.5 ${cfg.color} ${cfg.glow}`} />
      </span>
      <span className="text-[10px] text-muted-foreground font-mono uppercase tracking-wider" data-testid="server-status-label">
        SERVER {cfg.label}
      </span>
      {latency !== null && status !== "checking" && (
        <span className="ml-auto text-[10px] text-muted-foreground/60 font-mono">{latency}ms</span>
      )}
    </div>
  );
};
